import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { LayoutGrid, Sparkles, Settings as Cog, Apple, MessageCircle, Ghost, Swords, Plus, Coffee, Zap, X } from "lucide-react";
import api from "@/api";
import { prefetch } from "@/lib/pageCache";
import { warmChunk } from "@/lib/prefetchChunks";
import { isEnabled } from "@/config/features";
const BASE_ITEMS = [{
  to: "/dashboard",
  icon: LayoutGrid,
  key: "nav.dashboard",
  test: "nav-dashboard"
},
// SHADOW — the player's AI Twin / shadow self.
{
  to: "/battles/shadow",
  icon: Ghost,
  key: "nav.shadow",
  test: "nav-shadow"
},
// CHATS — WhatsApp-style room list. Challenges & meetups leven ook als
// rooms hier binnen, dus geen dedicated tab meer (Feb 2026 IA reshuffle
// naar 2-2 layout rond de centrale FAB). Toegang tot de unified
// challenges-feed loopt nu via de Dashboard-card of via de FAB.
{
  to: "/chats",
  icon: MessageCircle,
  key: "nav.chats",
  test: "nav-chats"
}, {
  to: "/settings",
  icon: Cog,
  key: "nav.settings",
  test: "nav-settings"
}];

// Modules the user can pin as a 5th nav slot via a coach proposal. We
// keep the list explicit so an LLM hallucinating a random module name
// can never render an unknown icon in the nav.
const EXTRA_NAV_MAP = {
  nutrition: {
    to: "/nutrition",
    icon: Apple,
    key: "nav.nutrition",
    test: "nav-nutrition"
  }
};

// Map nav target → list of [cacheKey, urlBuilder] tuples so we can warm
// the cache the moment the user touches a tab (on iOS the touchstart event
// fires ~80-150 ms before the click resolves the navigation).
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
const PREFETCHERS = {
  "/dashboard": [["dashboard:day:today", () => api.get(`/health/day?date=${todayISO()}`).then(r => r.data)], ["dashboard:week:today", () => api.get(`/health/week?ref_date=${todayISO()}`).then(r => r.data)], ["coach:latest", () => api.get("/coach/latest").then(r => r.data).catch(() => null)]],
  "/nutrition": [["nutrition:today", () => api.get("/nutrition/today").then(r => r.data)], ["nutrition:meals", () => api.get("/nutrition/meals").then(r => r.data)]],
  "/coach": [["coach:latest", () => api.get("/coach/latest").then(r => r.data).catch(() => null)], ["coach:insights", () => api.get("/coach/insights").then(r => r.data).catch(() => null)]],
  "/social": [["social:challenges", () => api.get("/social/challenges").then(r => r.data)], ["social:leaderboard", () => api.get("/social/leaderboard").then(r => r.data)], ["social:streak", () => api.get("/social/streak").then(r => r.data)]],
  "/crew-map": [["map:crew", () => api.get("/geo/crew").then(r => r.data).catch(() => ({
    pins: []
  }))], ["map:discover", () => api.get("/geo/discover").then(r => r.data).catch(() => ({
    pins: []
  }))]],
  "/chats": [["chats:rooms", () => api.get("/chats/rooms").then(r => r.data).catch(() => ({
    items: []
  }))]],
  "/settings": [["settings:me", () => api.get("/settings/me").then(r => r.data)]]
};
function warmTab(to) {
  // Kick off the JS chunk fetch FIRST — it's the longest-pole task (~50-150 kB
  // gzipped per route) and it can happen in parallel with the data prefetch.
  warmChunk(to);
  const ps = PREFETCHERS[to];
  if (!ps) return;
  ps.forEach(([k, fetcher]) => prefetch(k, fetcher).catch(() => {}));
}
export default function BottomNav() {
  const {
    pathname
  } = useLocation();
  const {
    t
  } = useTranslation();
  // Full-screen (chat-style) routes under a specific challenge:
  //   /challenges/:id           → detail page (own header, no nav)
  //   /challenges/:id/chat      → WhatsApp-style chat (must hide nav)
  //   /challenges/:id/info/:mid → message info (must hide nav)
  //   /challenges/:id/members   → members list
  //   /challenges/:id/leaderboard → full leaderboard
  // We match any sub-path so a stray sub-route can never leak the nav
  // behind the keyboard.  `/challenges` (list) is intentionally NOT
  // matched — the trailing `/[^/]+` requires an id segment.
  const hide = pathname === "/" || pathname === "/login" || pathname === "/register" || pathname.startsWith("/auth/") || pathname.startsWith("/onboarding") || pathname.startsWith("/promo") || pathname.startsWith("/rescue") || /^\/grocery\/[^/]+\/shop$/.test(pathname) || /^\/chats\/[^/]+$/.test(pathname) || /^\/challenges\/[^/]+(\/.*)?$/.test(pathname);

  // Read the user's pinned extra-nav choice from /settings/me.
  // Coach proposals can call /coach/chat/proposal/apply with kind
  // `add_bottom_nav` to set `bottom_nav_extra` to e.g. "nutrition".
  // NOTE: every hook must run before any early return — never gate
  // them behind `hide` or React will hit the rules-of-hooks invariant.
  const [extraKey, setExtraKey] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/settings/me").then(r => {
      if (cancelled) return;
      setExtraKey(r.data?.bottom_nav_extra || null);
    }).catch(() => {});
    load();
    const onPrefs = () => load();
    window.addEventListener("wyng:prefs-updated", onPrefs);
    return () => {
      cancelled = true;
      window.removeEventListener("wyng:prefs-updated", onPrefs);
    };
  }, []);
  const items = useMemo(() => {
    if (extraKey && EXTRA_NAV_MAP[extraKey]) {
      // Respect MVP feature flags — a stale `bottom_nav_extra` pref
      // (e.g. "nutrition" pinned before we killed the module) must
      // NOT resurrect a hidden feature.
      if (extraKey === "nutrition" && !isEnabled("NUTRITION")) {
        return BASE_ITEMS;
      }
      const out = [...BASE_ITEMS];
      const lastIdx = out.length - 1;
      out.splice(lastIdx, 0, EXTRA_NAV_MAP[extraKey]);
      return out;
    }
    return BASE_ITEMS;
  }, [extraKey]);

  // Coach tab badge — unread weekly AI reports. Lightweight polled
  // every 60s while the nav is mounted so the dot lights up moments
  // after the scheduler produces a fresh report.
  const [coachBadge, setCoachBadge] = useState(0);
  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const {
          data
        } = await api.get("/coach/weekly-report/unread-count");
        if (!cancel) setCoachBadge(data?.count || 0);
      } catch {/* silent */}
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, []);

  if (hide) return null;
  return <>
    <nav className="bottom-nav flex justify-around items-center px-4" data-testid="bottom-nav">
            {items.map(({
      to,
      icon: Icon,
      key,
      test
    }, idx) => {
      const active = pathname.startsWith(to);
      // After the second slot (Twin, index 1), inject an invisible
      // spacer so the centered FAB has room. The FAB itself renders
      // outside the nav via a sibling absolute-positioned element
      // (see below) so its 56px circle can float above the bar.
      // With the Feb 2026 "2-2 layout" reshuffle we now have exactly
      // 4 nav items → 2 links · FAB · 2 links.
      const link = <NavLink key={to} to={to} data-testid={test}
      onPointerDown={() => warmTab(to)} onTouchStart={() => warmTab(to)} onMouseEnter={() => warmTab(to)} onClick={() => {
        if (active) {
          try {
            window.dispatchEvent(new CustomEvent("wyng:nav-reactivate", {
              detail: {
                path: to
              }
            }));
          } catch { /* dispatch failed — non-fatal */ }
        }
      }} className="flex flex-col items-center gap-1 no-tap-highlight">
                        <div className="relative">
                            <Icon size={22} strokeWidth={active ? 2 : 1.5} className={active ? "text-foreground" : "text-foreground/40"} />
                            {to === "/coach" && coachBadge > 0 && <span data-testid="nav-coach-badge" className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px]
                                               px-1 rounded-full bg-[#FFD60A] text-black
                                               text-[10px] font-bold flex items-center justify-center
                                               shadow-[0_0_8px_rgba(255,214,10,0.6)]">
                                    {coachBadge}
                                </span>}
                        </div>
                        <span className={`text-[10px] font-semibold tracking-wide ${active ? "text-foreground" : "text-foreground/40"}`}>
                            {t(key)}
                        </span>
                    </NavLink>;
      if (idx === 1) {
        // Placeholder slot preserving symmetric spacing for the FAB.
        return <React.Fragment key={to}>
          {link}
          <div key="fab-spacer" aria-hidden className="w-[56px] shrink-0" />
        </React.Fragment>;
      }
      return link;
    })}
        </nav>
    {/* Central FAB — hovers above the nav bar, opens the "Start something"
        sheet with three one-tap primary actions (Compete / Meet up /
        Walk together nu).  Sheet lives in `ActionFAB` component. */}
    <ActionFAB />
  </>;
}


// ─── Central FAB — "Start something" ─────────────────────────────────
//
// Per the Feb 2026 IA reshuffle, the middle slot of the bottom nav is a
// floating action button.  Tapping it opens a bottom-sheet with the
// three primary "kick something off" flows:
//
//   ⚔️  Compete       → navigate to `/challenges?new=1` (create wizard)
//   ☕  Meet up        → navigate to `/crew-map?meetup=1` (crew-map handles
//                       the meetup planner in-context)
//   ⚡  Walk together  → navigate to `/crew-map?walk=now` (spontaneous
//                       right-now walk invite, hero-loop for Steptember)
//
// The sheet uses a native-feeling backdrop + slide-up animation and
// closes on backdrop tap, ESC key, or after any tile is picked.
function ActionFAB() {
    const [open, setOpen] = useState(false);
    const nav = useNavigate();
    const { pathname } = useLocation();

    // Hide the FAB on the same screens as the nav (chat rooms etc.) —
    // parent already returns null in those cases, but be defensive.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const go = (path) => {
        setOpen(false);
        // Small delay so the sheet close animation is visible before the
        // next screen mounts — 120ms feels crisp without being sluggish.
        setTimeout(() => nav(path), 120);
    };

    return (
        <>
            <button
                data-testid="nav-fab"
                aria-label="Start iets"
                onClick={() => setOpen(v => !v)}
                className="fixed left-1/2 -translate-x-1/2
                           w-[56px] h-[56px] rounded-full
                           flex items-center justify-center
                           bg-[#FFD60A] text-black
                           shadow-[0_6px_20px_rgba(255,214,10,0.55),0_0_0_4px_rgba(0,0,0,0.9)]
                           active:scale-95 transition
                           z-[55]"
                style={{
                    // FAB zit half in de notched dock: center op de
                    // top-edge van de nav.  Nav bottom = safe + 14,
                    // nav height = 64 → top = safe + 78.  FAB 56px
                    // → bottom = 78 - 28 = 50 lijnt de FAB-center
                    // exact uit met de nav-top edge, en het onderste
                    // stukje van de FAB zit 4px vrij in de notch
                    // gutter (radius 34 - 28 = 6).  Voelt als een
                    // premium half-floating dock.
                    bottom: "calc(env(safe-area-inset-bottom) + 50px)",
                }}
            >
                <span className={`transition-transform duration-200 ${open ? "rotate-45" : ""}`}>
                    <Plus size={26} strokeWidth={3} />
                </span>
            </button>

            {/* Bottom sheet */}
            {open && (
                <div
                    data-testid="nav-fab-sheet"
                    className="fixed inset-0 z-[70]"
                    onClick={() => setOpen(false)}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/55 backdrop-blur-sm animate-fade-in" />
                    {/* Sheet */}
                    <div
                        className="absolute inset-x-0 bottom-0 bg-card border-t border-border
                                   rounded-t-3xl p-5 pb-8 animate-slide-up"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mx-auto w-10 h-1 rounded-full bg-foreground/15 mb-4" />
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-black tracking-tight">Start iets</h3>
                            <button
                                data-testid="nav-fab-close"
                                onClick={() => setOpen(false)}
                                className="w-9 h-9 rounded-full flex items-center justify-center
                                           active:bg-foreground/10 transition"
                                aria-label="Sluiten"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-[12.5px] text-muted-foreground mb-4">
                            Wat wil je op poten zetten met je crew?
                        </p>
                        <div className="space-y-2.5">
                            <ActionTile
                                testid="nav-fab-compete"
                                icon={Swords}
                                accent="#FFD60A"
                                title="Competitie starten"
                                desc="Duel of team-challenge, jij vs vrienden op stappen."
                                onClick={() => go("/challenges?new=1")}
                            />
                            <ActionTile
                                testid="nav-fab-meetup"
                                icon={Coffee}
                                accent="#00EBD4"
                                title="Meetup plannen"
                                desc="Koffie, wandeling of borrel — kies tijd en plek."
                                onClick={() => go("/crew-map?meetup=1")}
                            />
                            <ActionTile
                                testid="nav-fab-walk"
                                icon={Zap}
                                accent="#7C4DFF"
                                title="Nu samen lopen"
                                desc="Spontane wandeling — wie loopt er nu mee?"
                                onClick={() => go("/crew-map?walk=now")}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function ActionTile({ testid, icon: Icon, accent, title, desc, onClick }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className="w-full flex items-center gap-3 p-3.5 rounded-2xl
                       bg-background/60 border border-border
                       active:scale-[0.98] transition text-left
                       hover:bg-background/80"
        >
            <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{
                    background: `${accent}22`,
                    color: accent,
                    boxShadow: `inset 0 0 0 1px ${accent}44`,
                }}
            >
                <Icon size={20} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-[14.5px] leading-tight">{title}</div>
                <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">{desc}</div>
            </div>
            <span className="text-foreground/40 text-lg leading-none">›</span>
        </button>
    );
}
